import { ApiError } from '@/api/client'

/**
 * Operator-facing classification of a failed API call. Pure — no UI.
 * Used by ErrorState so every list page distinguishes the same failure
 * classes the same way.
 */
export type ApiFailureKind =
  | 'unauthenticated' // 401 (session expired / refresh failed)
  | 'forbidden' // 403
  | 'not_found' // 404
  | 'client' // other 4xx (400/409/422/...)
  | 'server' // 5xx
  | 'network' // fetch rejected: offline, DNS, CORS, aborted
  | 'unknown'

export interface ApiFailure {
  kind: ApiFailureKind
  status: number | undefined
  /** Machine code from the error envelope, e.g. platform_scope_not_allowed. */
  code: string | undefined
  /** The server's message for 4xx responses only; always undefined for 5xx / network / unknown. */
  serverMessage: string | undefined
  requestId: string | undefined
}

export function classifyApiError(error: unknown): ApiFailure {
  if (error instanceof ApiError) {
    const s = error.status
    let kind: ApiFailureKind = 'unknown'
    if (s === 401) kind = 'unauthenticated'
    else if (s === 403) kind = 'forbidden'
    else if (s === 404) kind = 'not_found'
    else if (s >= 500) kind = 'server'
    else if (s >= 400) kind = 'client'
    // ApiError falls back to "<status> <statusText>" when the body has no
    // message — that is not a server-authored message, so drop it.
    const generic = /^\d{3}(\s|$)/.test(error.message)
    return {
      kind,
      status: s,
      code: error.code,
      // Only 4xx messages are surfaced: some 5xx bodies carry raw err.Error()
      // text (SQL, driver errors) that must never reach the operator.
      serverMessage:
        generic || error.message === '' || s < 400 || s >= 500 ? undefined : error.message,
      requestId: error.requestId,
    }
  }
  if (error instanceof TypeError) {
    // fetch() rejects with TypeError on network-level failure.
    return { kind: 'network', status: undefined, code: undefined, serverMessage: undefined, requestId: undefined }
  }
  return { kind: 'unknown', status: undefined, code: undefined, serverMessage: undefined, requestId: undefined }
}

/** Backend code for a platform-scope token calling a /tenants/self/* route. */
export const PLATFORM_SCOPE_NOT_ALLOWED = 'platform_scope_not_allowed'
